import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { User, DocumentType } from '../types';
import { api } from '../services/api';
import { analyzeDocument } from '../services/ai-service';
import { sanitizeForStorage } from '../utils/textUtils';
import {
    X,
    Upload,
    FileText,
    CheckCircle,
    AlertTriangle,
    Loader2,
    Calendar,
    ArrowRight,
    ArrowLeft,
    Trash2,
    Search,
    AlertCircle
} from 'lucide-react';
import { LocalizedDatePicker } from './ui/LocalizedDatePicker';

interface BulkUploadWizardProps {
    user: User;
    onClose: () => void;
    onSuccess: () => void;
}

type WizardStep = 'client_select' | 'file_upload' | 'review';

interface PendingFile {
    id: string;
    file: File;
    title: string;
    typeId: string;
    status: 'pending' | 'uploading' | 'success' | 'error';
    errorMessage?: string;
    periodStart?: string; // Optional period for this specific file
    periodEnd?: string;
}

export const BulkUploadWizard: React.FC<BulkUploadWizardProps> = ({ user, onClose, onSuccess }) => {
    const { t } = useTranslation('documents');
    const [step, setStep] = useState<WizardStep>('client_select');

    // Step 1: Client Selection State
    const [clients, setClients] = useState<User[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const [clientSearch, setClientSearch] = useState('');

    // Step 2 & 3: File & Metadata State
    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const [types, setTypes] = useState<DocumentType[]>([]);

    // Global Settings for the batch (optional, to apply to all)
    const [globalPeriodStart, setGlobalPeriodStart] = useState('');
    const [globalPeriodEnd, setGlobalPeriodEnd] = useState('');
    const [sendEmailNotification, setSendEmailNotification] = useState(false);

    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [agencyName, setAgencyName] = useState('');

    // Load Initial Data
    useEffect(() => {
        const load = async () => {
            setIsLoadingData(true);
            try {
                const agencyId = user.role === 'agency' ? user.id : user.agencyId;
                if (agencyId) {
                    const [agencyClients, docTypes] = await Promise.all([
                        api.getAgencyClients(agencyId, user.role === 'agency_member' ? user.id : undefined),
                        api.getDocumentTypes(agencyId)
                    ]);
                    setClients(agencyClients);
                    setTypes(docTypes);
                }

                // Fetch Agency Name
                if (user.role === 'agency') {
                    setAgencyName(user.profile?.companyName || user.config?.companyName || 'Agency');
                } else if (user.role === 'agency_member' && user.agencyId) {
                    try {
                        // Use Backend Endpoint
                        const profile = await api.getPublicProfile(user.agencyId);
                        setAgencyName(profile.companyName || 'Agency');
                    } catch (e) {
                        setAgencyName('Agency');
                    }
                } else {
                    setAgencyName('Agency');
                }

            } catch (error) {
                console.error("Failed to load data", error);
            } finally {
                setIsLoadingData(false);
            }
        };
        load();
    }, [user.id]);

    // Helpers
    const selectedClient = clients.find(c => c.id === selectedClientId);

    const handleFileDrop = (e: React.DragEvent | React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        let files: FileList | null = null;
        if ('dataTransfer' in e) {
            files = e.dataTransfer.files;
        } else {
            files = (e.target as HTMLInputElement).files;
        }

        if (files) {
            const newPendingFiles: PendingFile[] = Array.from(files).map(file => ({
                id: Math.random().toString(36).substring(7),
                file,
                title: file.name,
                typeId: '', // User must select
                status: 'pending'
            }));
            setPendingFiles(prev => [...prev, ...newPendingFiles]);
            if (step === 'file_upload' && newPendingFiles.length > 0) {
                // Auto advance if we just dropped files? Maybe not, allow adding more.
                // But definitely clear if they want to.
            }
        }
    };

    const removeFile = (id: string) => {
        setPendingFiles(prev => prev.filter(f => f.id !== id));
    };

    const updateFileMetadata = (id: string, updates: Partial<PendingFile>) => {
        setPendingFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
    };

    const handleSave = async () => {
        setIsUploading(true);
        const client = selectedClient;
        if (!client) return;

        // Process sequentially or parallel? Parallel is limited by browser usually, but 3-5 is fine.
        // Let's do a loop for simplicity and progress tracking.

        const timestamp = Date.now();
        const agencyId = user.role === 'agency' ? user.id : user.agencyId;
        const agencyFolder = agencyId;
        const clientFolder = client.id;
        const year = new Date().getFullYear();

        let successCount = 0;

        const uploadedDocs: Array<{ title: string; typeName: string; typeId?: string; date: string }> = [];

        for (const pFile of pendingFiles) {
            if (pFile.status === 'success') {
                // Even if already success, we might want to include in email if we are re-running for failures?
                // But typically re-run implies new notification for new files. 
                // If it was already success from previous run, we shouldn't re-upload or re-notify probably.
                continue;
            }

            // Update status to uploading
            updateFileMetadata(pFile.id, { status: 'uploading' });

            try {
                const typeItem = types.find(t => t.id === pFile.typeId);

                // Human-readable folder structure
                // Human-readable folder structure
                const agencyNameRaw = agencyName || 'Agency'; // Use fetched name
                const clientNameRaw = client?.profile?.companyName || client?.config?.companyName || client?.email || 'Unknown';
                const typeNameRaw = typeItem?.name || t('common.other', { defaultValue: 'Other' });

                const agencyFolder = sanitizeForStorage(agencyNameRaw);
                const clientFolder = sanitizeForStorage(clientNameRaw);
                const typeFolder = sanitizeForStorage(typeNameRaw);

                const uniqueFilename = `${timestamp}_${sanitizeForStorage(pFile.file.name)}`;
                const path = `documents/${agencyFolder}/${clientFolder}/${typeFolder}/${year}/${uniqueFilename}`;

                const metadata = { agencyId: agencyId, clientId: client.id };

                // 1. Upload
                const downloadUrl = await api.uploadDocumentFile(pFile.file, path, metadata);
                const typeName = typeItem?.name || t('common.unknown', { defaultValue: 'Unknown' });
                const docTitle = pFile.title || pFile.file.name;

                // AI ANALYSIS (Feature Flag Check && Document Type Check)
                let aiAnalysis = null;

                console.log(`[BulkUpload] Checking AI for ${pFile.file.name}:`, {
                    clientAIEnabled: client.config?.ai_features?.document_ai_enabled,
                    typeAIEnabled: typeItem?.aiOcrEnabled,
                    typeName: typeItem?.name
                });

                if (client.config?.ai_features?.document_ai_enabled && typeItem?.aiOcrEnabled) {
                    try {
                        console.log(`[BulkUpload] Starting AI Analysis for ${pFile.file.name}...`);
                        // Fix: Pass all 4 arguments: file, docId (placeholder), companyId
                        // We use 'PENDING' for docId because we don't have it yet. The backend/API will assign the real ID.
                        // apiKey param removed
                        aiAnalysis = await analyzeDocument(pFile.file, 'PENDING', client.id);
                        console.log(`[BulkUpload] AI Analysis Success for ${pFile.file.name}`);
                    } catch (aiError) {
                        console.error(`[BulkUpload] AI Analysis Failed for ${pFile.file.name}:`, aiError);
                        // We proceed without analysis rather than failing the whole upload
                    }
                } else if (!typeItem?.aiOcrEnabled) {
                    console.log(`[BulkUpload] Skipping AI Analysis for ${pFile.file.name} (Type "${typeItem?.name}" has AI OCR disabled)`);
                }

                // Sanitize aiAnalysis to remove undefined values before Firestore
                if (aiAnalysis) {
                    // recursively remove undefined? Or just JSON stringify/parse?
                    // Quickest way for Firestore compatibility:
                    aiAnalysis = JSON.parse(JSON.stringify(aiAnalysis));
                }

                // 2. Create Record
                await api.createDocument({
                    typeId: pFile.typeId || 'unknown',
                    typeName: typeName,
                    title: docTitle,
                    agencyId: agencyId,
                    clientId: client.id,
                    uploadedBy: user.id,
                    uploadedByRole: 'agency',
                    fileUrl: downloadUrl,
                    storagePath: path,
                    fileType: pFile.file.type,
                    size: pFile.file.size,
                    status: 'approved',
                    version: 1,
                    metadata: {
                        periodStart: pFile.periodStart || globalPeriodStart || undefined,
                        periodEnd: pFile.periodEnd || globalPeriodEnd || undefined
                    },
                    ai_analysis: aiAnalysis || null
                });

                updateFileMetadata(pFile.id, { status: 'success' });
                successCount++;
                uploadedDocs.push({
                    title: docTitle,
                    typeName: typeName,
                    typeId: pFile.typeId,
                    date: new Date().toLocaleDateString()
                });

            } catch (error: any) {
                console.error(error);
                updateFileMetadata(pFile.id, { status: 'error', errorMessage: error.message });
            }
        }

        // Send Notification if we uploaded anything new
        if (uploadedDocs.length > 0 && sendEmailNotification) {
            api.sendDocumentNotification({
                clientId: client.id,
                docTitle: t('messages.new_docs_count', { count: uploadedDocs.length, defaultValue: `${uploadedDocs.length} New Document(s)` }),
                docType: t('wizard.title'),
                agencyName: user.profile?.companyName || user.config?.companyName || 'Agency',
                documents: uploadedDocs
            }).catch(console.error);
        }

        setIsUploading(false);

        // If all success, close logic
        const allSuccess = pendingFiles.every(f => f.status === 'success' || (f.status as any) === 'success' /* check updated state? pendingFiles is stale in loop? No, setState doesn't update 'pendingFiles' in scope immediately. */);

        // We need to check if we have any errors left.
        // Since we can't easily check the *current* state inside this closure without refs or functional updates which we used,
        // let's rely on the fact that we updated them. 
        // Actually, for better UX, we should wait for user to acknowledge success if 100%.

        if (successCount === pendingFiles.length) {
            setTimeout(() => {
                onSuccess();
            }, 1000); // Short delay to show green checks
        }
    };

    // Filter Clients
    const filteredClients = clients.filter(c => {
        const q = clientSearch.toLowerCase();
        return (c.profile?.companyName || '').toLowerCase().includes(q)
            || (c.profile?.vatNumber || '').toLowerCase().includes(q)
            || c.email.toLowerCase().includes(q);
    });

    // RENDERERS

    if (isLoadingData) {
        return <div className="p-12 flex justify-center"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col items-center min-h-[500px]">

            {/* Header / Stepper */}
            <div className="w-full border-b border-slate-100 p-6 flex justify-between items-center bg-slate-50">
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className="hover:bg-slate-200 p-2 rounded-full transition"><ArrowLeft size={20} /></button>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">{t('wizard.title')}</h2>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <span className={step === 'client_select' ? 'font-bold text-blue-600' : ''}>{t('wizard.step_1')}</span>
                            <ArrowRight size={14} />
                            <span className={step === 'file_upload' ? 'font-bold text-blue-600' : ''}>{t('wizard.step_2')}</span>
                            <ArrowRight size={14} />
                            <span className={step === 'review' ? 'font-bold text-blue-600' : ''}>{t('wizard.step_3')}</span>
                        </div>
                    </div>
                </div>
                {selectedClient && step !== 'client_select' && (
                    <div className="text-right">
                        <div className="font-bold text-slate-900">{selectedClient.profile?.companyName}</div>
                        <div className="text-xs text-slate-500">{selectedClient.profile?.vatNumber}</div>
                    </div>
                )}
            </div>

            {/* Content Body */}
            <div className="w-full max-w-4xl p-8 flex-1">

                {/* STEP 1: CLIENT SELECT */}
                {step === 'client_select' && (
                    <div className="space-y-6">
                        <div className="max-w-xl mx-auto">
                            <h3 className="text-2xl font-bold text-center mb-6">{t('wizard.step_1_title')}</h3>
                            <div className="relative mb-4">
                                <Search className="absolute left-4 top-3.5 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder={t('wizard.search_placeholder')}
                                    value={clientSearch}
                                    onChange={e => setClientSearch(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl text-lg outline-none focus:ring-2 focus:ring-black shadow-sm"
                                    autoFocus
                                />
                            </div>
                            <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto shadow-sm">
                                {filteredClients.length === 0 ? (
                                    <div className="p-8 text-center text-slate-400">{t('wizard.no_clients')}</div>
                                ) : (
                                    filteredClients.map(client => (
                                        <div
                                            key={client.id}
                                            onClick={() => {
                                                setSelectedClientId(client.id);
                                                setStep('file_upload');
                                                setClientSearch(''); // Clear search for next time? Or keep?
                                            }}
                                            className="p-4 border-b border-slate-100 last:border-0 hover:bg-blue-50 cursor-pointer transition flex justify-between items-center group"
                                        >
                                            <div>
                                                <div className="font-bold text-slate-900 group-hover:text-blue-700">{client.profile?.companyName || client.config.companyName || client.email}</div>
                                                <div className="text-sm text-slate-500 group-hover:text-blue-500">VAT: {client.profile?.vatNumber || 'N/A'}</div>
                                            </div>
                                            <ArrowRight className="text-slate-300 group-hover:text-blue-600 opacity-0 group-hover:opacity-100 transition" size={20} />
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 2: FILE UPLOAD */}
                {step === 'file_upload' && (
                    <div className="h-full flex flex-col items-center justify-center space-y-8 animate-fadeIn">
                        <div
                            className="w-full max-w-2xl border-4 border-dashed border-slate-200 rounded-3xl p-12 text-center hover:bg-slate-50 hover:border-blue-300 transition-all cursor-pointer group"
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleFileDrop}
                            onClick={() => document.getElementById('bulk-file-input')?.click()}
                        >
                            <input id="bulk-file-input" type="file" multiple className="hidden" onChange={handleFileDrop} />
                            <div className="w-20 h-20 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                <Upload size={40} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('wizard.drag_drop')}</h3>
                            <p className="text-slate-500 text-lg">{t('wizard.browse')}</p>
                            <p className="text-sm text-slate-400 mt-4">{t('wizard.supported_formats')}</p>
                        </div>

                        {pendingFiles.length > 0 && (
                            <div className="w-full max-w-2xl">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="font-bold text-slate-700">{pendingFiles.length} {t('wizard.files_selected')}</span>
                                    <button onClick={() => setPendingFiles([])} className="text-red-500 text-sm hover:underline">{t('wizard.clear_all')}</button>
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                    {pendingFiles.slice(0, 5).map(f => (
                                        <div key={f.id} className="bg-slate-50 p-3 rounded-lg flex justify-between items-center">
                                            <span className="truncate max-w-[80%] text-sm font-medium">{f.file.name}</span>
                                            <button onClick={(e) => { e.stopPropagation(); removeFile(f.id); }} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
                                        </div>
                                    ))}
                                    {pendingFiles.length > 5 && (
                                        <div className="text-center text-sm text-slate-500 italic">{t('wizard.more_files', { count: pendingFiles.length - 5 })}</div>
                                    )}
                                </div>
                                <div className="text-center mt-8">
                                    <button
                                        onClick={() => setStep('review')}
                                        className="bg-black text-white px-8 py-3 rounded-xl font-bold text-lg hover:scale-105 transition shadow-xl"
                                    >
                                        {t('wizard.continue_review', { count: pendingFiles.length })}
                                    </button>
                                </div>
                            </div>
                        )}
                        <button onClick={() => setStep('client_select')} className="text-slate-400 font-bold hover:text-slate-600">{t('wizard.back_to_client')}</button>
                    </div>
                )}

                {/* STEP 3: REVIEW & ASSIGN */}
                {step === 'review' && (
                    <div className="h-full flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold">{t('wizard.review_docs')}</h3>
                            <div className="flex gap-4">
                                <button onClick={() => setStep('file_upload')} className="text-slate-500 font-bold hover:bg-slate-100 px-4 py-2 rounded-lg">{t('wizard.add_more')}</button>
                                {/* Global quick apply could go here */}
                            </div>
                        </div>

                        {/* Global Settings */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-6 flex flex-wrap gap-4 items-end">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">{t('wizard.global_start')}</label>
                                <LocalizedDatePicker value={globalPeriodStart} onChange={setGlobalPeriodStart} format={user.dateFormat} className="w-full text-sm border-slate-200 rounded-lg p-2" />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">{t('wizard.global_end')}</label>
                                <LocalizedDatePicker value={globalPeriodEnd} onChange={setGlobalPeriodEnd} format={user.dateFormat} className="w-full text-sm border-slate-200 rounded-lg p-2" />
                            </div>
                            <div className="flex items-center gap-2 pb-2">
                                <input
                                    type="checkbox"
                                    id="notifyClient"
                                    checked={sendEmailNotification}
                                    onChange={e => setSendEmailNotification(e.target.checked)}
                                    className="w-5 h-5 accent-black"
                                />
                                <label htmlFor="notifyClient" className="text-sm font-bold cursor-pointer">{t('wizard.notify_client')}</label>
                            </div>
                        </div>

                        {/* File Table */}
                        <div className="flex-1 overflow-y-auto min-h-[300px] border border-slate-200 rounded-xl">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3">{t('wizard.file_name')}</th>
                                        <th className="px-4 py-3 w-[25%]">{t('wizard.doc_type')}</th>
                                        <th className="px-4 py-3 w-[30%]">{t('wizard.display_title')}</th>
                                        <th className="px-4 py-3 w-[10%] text-right">{t('wizard.action')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {pendingFiles.map(file => (
                                        <tr key={file.id} className={file.status === 'error' ? 'bg-red-50' : 'hover:bg-slate-50'}>
                                            <td className="px-4 py-3 font-medium text-slate-700 truncate max-w-[200px]" title={file.file.name}>
                                                {file.status === 'uploading' && <Loader2 size={14} className="inline animate-spin mr-2 text-blue-500" />}
                                                {file.status === 'success' && <CheckCircle size={14} className="inline mr-2 text-green-500" />}
                                                {file.status === 'error' && <AlertCircle size={14} className="inline mr-2 text-red-500" />}
                                                {file.file.name}
                                                {file.errorMessage && <div className="text-xs text-red-500 max-w-[200px]">{file.errorMessage}</div>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={file.typeId}
                                                    onChange={e => updateFileMetadata(file.id, { typeId: e.target.value })}
                                                    className={`w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black ${!file.typeId ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
                                                    disabled={file.status === 'success' || file.status === 'uploading'}
                                                >
                                                    <option value="">{t('wizard.choose_type')}</option>
                                                    {types.map(tOption => (
                                                        <option key={tOption.id} value={tOption.id}>
                                                            {tOption.nameTranslations?.[user.language as 'en' | 'el'] || tOption.nameTranslations?.en || tOption.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={file.title}
                                                    onChange={e => updateFileMetadata(file.id, { title: e.target.value })}
                                                    className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-black"
                                                    disabled={file.status === 'success' || file.status === 'uploading'}
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {file.status !== 'success' && file.status !== 'uploading' && (
                                                    <button onClick={() => removeFile(file.id)} className="text-slate-400 hover:text-red-500 p-2"><Trash2 size={16} /></button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Actions */}
                        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
                            <span className="text-sm text-slate-400 self-center mr-auto">
                                {t('messages.selected_files_count', { count: pendingFiles.filter(f => f.typeId).length, total: pendingFiles.length, defaultValue: `${pendingFiles.filter(f => f.typeId).length} / ${pendingFiles.length} types assigned` })}
                            </span>
                            <button onClick={() => setStep('file_upload')} disabled={isUploading} className="px-6 py-2 font-bold text-slate-500 hover:bg-slate-100 rounded-lg">{t('common.back')}</button>
                            <button
                                onClick={handleSave}
                                disabled={isUploading || pendingFiles.some(f => !f.typeId)}
                                className="bg-black text-white px-8 py-2 rounded-lg font-bold hover:scale-105 transition shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:scale-100"
                            >
                                {isUploading ? <Loader2 className="animate-spin" /> : <CheckCircle size={18} />}
                                {isUploading ? t('wizard.uploading') : t('wizard.upload_all', { count: pendingFiles.length })}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
